import { getErrorCode } from '@onefootprint/fetch-hosted';
import type { ApiError } from '@onefootprint/request-types';
import { UnsupportedRequirementError } from '@/idv2/utils/unsupported-requirement-error';

const CONNECTION_ERROR_MESSAGES = ['failed to fetch', 'load failed', 'networkerror when attempting to fetch'];

const ERROR_CODE_MAP: Record<string, ErrorKey> = {
  E117: 'sessionNotFound',
  E118: 'sessionExpired',
  E119: 'sessionInvalid',
  E124: 'businessNotOwnedByUser',
  E140: 'onboardingSuperseded',
};

export type ErrorKey =
  | 'connectionError'
  | 'genericError'
  | 'sessionNotFound'
  | 'sessionExpired'
  | 'sessionInvalid'
  | 'businessNotOwnedByUser'
  | 'onboardingSuperseded'
  | 'unsupportedRequirement';

export const getErrorKey = (error: Error | ApiError | null): ErrorKey => {
  if (error instanceof UnsupportedRequirementError) {
    return 'unsupportedRequirement';
  }

  const errorMessage = error?.message?.toLowerCase() ?? '';

  if (CONNECTION_ERROR_MESSAGES.some(message => errorMessage.includes(message))) {
    return 'connectionError';
  }

  // 404s in beforeLoad are transient: our backend's actix wildcard returns
  // "Endpoint not found" for any unrecognised method/path combination, which can
  // happen when an iOS Safari prefetch / network probe issues a GET against a
  // POST-only endpoint. Treat as connectionError so the boundary stays in
  // ERROR_TYPES_TO_IGNORE and the user sees a retryable screen instead of
  // "Something went wrong".
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  if (status === 404) {
    return 'connectionError';
  }

  const errorCode = getErrorCode(error);
  if (errorCode && errorCode in ERROR_CODE_MAP) {
    return ERROR_CODE_MAP[errorCode];
  }

  return 'genericError';
};
